import { Address } from './Address';
import { DependentRelationship } from './Common';
import { IDocument } from './Documents';

interface ICaseQuestionnaire {
  questionnaireId: number;
  caseQuestionnaireId: number;
  questionnaireName: string;
  questionnaireStatus: string;
  updatedDate?: string | null;
}

interface IDependent {
  dependentId: number;
  dependentFullName: string;
  dependentRelationship: DependentRelationship;
}

export interface IGovernmentCaseNumber {
  beneId: number;
  caseDependentId: number;
  fullName: string;
  governmentCaseNumber: string;
}

export enum Stage_Status {
  COMPLETED = 'COMPLETED',
  STARTED = 'STARTED',
  NOT_STARTED = 'NOT_STARTED',
}

export interface ICaseStage {
  name: string;
  businessDaysToCompletion: string;
  detailedStatuses: string[];
  startDate: string;
  status: Stage_Status;
  completionDate: string;
  notes: {
    id: number;
    detail: string;
    date: string;
  }[];
}

export interface IActiveCaseContactEmails {
  attorneyEmails: string[];
  assistantEmails: string[];
}

export interface IActiveCaseInfo extends ICaseInfo {
  companyType?: string | null;
  caseTypeApiKey?: string;
  preHireID?: string | null;
  categoryName?: string;
  categoryId?: number;
  categoryApiKey?: string | null;
  associatedCaseSubTypes?: {
    caseId: number;
    caseSubType: string;
    caseSubTypeId: number;
    associationId: number;
    caseSubTypeGroup: number;
    isActive: boolean;
    caseSubTypeApiKey?: string | null;
  }[];
  caseAssignmentType?: string | null;
  caseAssignmentTypeCode?: string | null;
  sponsorEntityId?: number | null;
  sponsorEntity?: string | null;
  otherSponsorEntity?: string | null;
  originCountry?: string;
  originCountryCode?: string;
  originCity?: string | null;
  caseCountryApiKey?: string;
  caseCity?: string | null;
  createdBy?: string;
  updatedBy?: string | null;
  closedDate?: string | null;
  closedDateOnly?: string | null;
  estimatedTravelDate?: string | null;
  requestedValidityDatesFrom?: string | null;
  requestedValidityDatesTo?: string | null;
  estimatedStartDate?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  confirmedReadyToStart?: boolean | null;
  createTimeStamp?: string | null;
  updatedTimeStamp?: string | null;
  reasonForChange?: string | null;
  reasonForChangeCode?: string | null;
  jobCode?: string;
  jobLevel?: string | null;
  jobDescription?: string;
  payrollLocation?: string | null;
  payrollLocationCode?: string | null;
  salary?: number;
  salaryCurrency?: string;
  frequencyOfSalary?: string;
  frequencyOfSalaryCode?: string;
  specialInstructions?: string | null;
  caseNumber?: string | null;
  clientNoteId?: number | null;
  npCompanyId?: number | null;
  followedStatus?: string | null;
  casePriority?: string | null;
  isBlocked?: boolean | null;
  isArchive?: boolean | null;
  managerContact?: string | null;
  hrContact?: string | null;
  caseAssistant?: string | null;
  caseAttorney?: string | null;
  npAssistant?: string | null;
  npAttorney?: string | null;
  staffId?: number | null;
  applicationSigner?: string | null;
  companyNumber?: string;
  beneficiaryNumber?: string;
  businessUnitId?: number;
  businessUnit?: string;
  primaryAttorneyEmail?: string;
  primaryAssistantEmail?: string;
  beneChanged?: boolean;
  dependentOnly?: boolean | null;
  progress?: number;
  caseInitiationSource?: string | null;
  gcpLink?: string | null;
  jobCatalogId?: number | null;
  enableCompanyJobCatalog?: boolean;
  associatedBlanketCases?: any | null;
  matterNumbers?: string[] | null;
  isBillable?: boolean | null;
  providerName?: string;
  parentCaseId?: number | null;
  childCount?: number;
  closedStatus?: string | null;
  isNotifiedSubmittedBillingForCloseCancelled?: boolean | null;
  internalCaseNotes?: string | null;
  profileManagerContactId?: number | null;
  profileManagerPhoneNumber?: string | null;
  profileManagerEmailAddress?: string | null;
  isAutoCarryForward?: boolean | null;
  isAutoCarryForwardDisplayed?: boolean | null;
  isVIP?: boolean;
  isBALSensitive?: boolean;
  preferredName?: string | null;
}

export interface ICaseInfo {
  caseQuestionnaires: ICaseQuestionnaire[];
  caseId: number;
  caseName: string;
  caseCountry: string;
  companyId: number;
  companyName: string;
  beneId: number;
  employeeName: string;
  isDependentOnly?: boolean | null;
  dependents: IDependent[];
  isActive: boolean;
  isAidi?: boolean;
  isNew?: boolean;
  createdDate: string;
  createdDateOnly: string;
  updatedDateOnly: string;
  caseStage: string;
  caseTypeId?: number;
  caseType?: string | null;
  updatedDate?: string;
  primaryAttorneyName?: string;
  primaryAssistantName?: string;
  category?: string;
  caseSubTypes: string[];
  employeeID?: string;
  balNumber?: string;
  workLocations?: Address[];
  jobPosition?: string;
  trackingNumber?: string;
  associatedCaseSubTypes?: { caseSubType: string; caseSubTypeId: number }[];
  caseCountryCode?: string;
  governmentCaseNumbers?: IGovernmentCaseNumber[];
  matterNumber?: string;
  sourceInstance: string;
  providerName?: string;
  immigrationProviderId?: number;
  isRemote?: boolean;
  universalCaseId?: string;
  remoteCaseId?: number;
}

export interface IQuestionnaire {
  questionnaire: string;
  updatedDate: string;
  questionnaireStatus: string;
  caseId: string;
  caseQuestionnaireId: number;
  relationship: string;
  assignDate: string;
  submittedDateOnly?: string;
  isComplete?: boolean;
  recipient?: string;
  recipientType?: string;
}

export interface ICaseAssociationModel extends ICaseInfo {
  caseCountryManager?: string;
  ccmReadOnly: boolean;
  closedDate?: string | null;
  closedDateOnly?: string | null;
  decisionDate?: string | null;
  dateOfMilestoneCompleted?: string;
  destinationCountry?: string;
  filedDate?: string | null;
  governmentCaseNumber?: string | null;
  isDenied: boolean;
  lastCompletedMilestone?: string;
  parentCaseId?: number | null;
  readOnly: boolean;
  subProjects: ICaseAssociationModel[] | null;
  sourceInstance: string;
}

export interface ICaseSubproject {
  caseType: string;
  caseNumber: string;
  openedDate: string;
  openedDateOnly: string;
  closedDate: string;
  closedDateOnly: boolean;
  primaryAttorneyName: string;
  primaryAssistantName: string;
  caseId: number;
  isActive: boolean;
  fullName: string;
  companyName: string;
  balNumber: string;
  subCaseType: string[];
  parentCaseId: number;
  notes: {
    reportNoteId: number;
    reportNotes: string;
    taskManagementId: number;
    postedDate: string;
    postedDateOnly: string;
    stageManagementId: number;
    ipgNodeId: string;
  }[];
  documentIds: number[];
}

export interface PassportInfo {
  passportId: number;
  passportNumber: string;
  passportExpirationDate: string;
  passportIssuedCountry: any;
  beneId: number;
  isArchive: boolean;
  dependentId: any;
  issuedDate: string;
  issuedState: string;
  issuedCity: string;
  hasEnoughBlanks: boolean;
  isTemporary: boolean;
  isBiometric: boolean;
  passportType: any;
  displayPassportType: any;
  locationOfPassport: any;
  displayLocationOfPassport: any;
  issuedToBene: any;
  issuedTo: number;
  country: string;
  issuingAuthority: string;
  isInactive: boolean;
  docId: number;
  docName: string;
  hasAttachment: boolean;
  isUsed: boolean;
  isRemote?: boolean;
  documents: IDocument[];
}

export interface ICaseStageResponse {
  caseId: number;
  stage: string;
  updatedAt: string;
}
